import React, { useEffect, useState } from "react";
import { collection, getDocs, query, where, addDoc, deleteDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import { db, auth } from "../../lib/firebase";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { format, addDays, startOfWeek } from "date-fns";

// Utility function to convert 24-hour time to 12-hour format
const formatTime12Hour = (time24: string): string => {
  if (!time24) return "";
  
  const [hours, minutes] = time24.split(':');
  const hour24 = parseInt(hours, 10);
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  
  return `${hour12}:${minutes} ${ampm}`;
};

// Compact time format for narrow displays
const formatTimeCompact = (time24: string): string => {
  if (!time24) return "";
  
  const [hours, minutes] = time24.split(':');
  const hour24 = parseInt(hours, 10);
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const ampm = hour24 >= 12 ? 'p' : 'a';
  
  // Remove leading zero from minutes and use shorter am/pm
  return `${hour12}:${minutes}${ampm}`;
};

interface TimeSlot {
  id: string;
  branch: string;
  dayOfWeek: string; // monday, tuesday, etc.
  startTime: string; // HH:MM format
  endTime: string; // HH:MM format
  maxStudents: number;
  createdAt: any;
}

interface ScheduledSlot {
  id: string;
  timeSlotId: string;
  studentId: string;
  date: string; // YYYY-MM-DD format
  status: string;
}

const daysOfWeek = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

const StaffTimeSlots: React.FC = () => {
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [scheduledSlots, setScheduledSlots] = useState<ScheduledSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [branch, setBranch] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [slotToCopy, setSlotToCopy] = useState<TimeSlot | null>(null);
  const [slotToEdit, setSlotToEdit] = useState<TimeSlot | null>(null);
  
  // Form states
  const [newSlot, setNewSlot] = useState({
    dayOfWeek: "",
    startTime: "",
    endTime: "",
    maxStudents: 1
  });

  const [copyToDays, setCopyTodays] = useState<string[]>([]);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [editSlot, setEditSlot] = useState({
    dayOfWeek: "",
    startTime: "",
    endTime: "",
    maxStudents: 1
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Get current staff user's branch
        if (auth.currentUser) {
          const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
          const userData = userDoc.data();
          if (userData && userData.branch) {
            const branchDoc = await getDoc(doc(db, "branches", userData.branch));
            const branchData = branchDoc.data();
            setBranch(branchData?.name || userData.branch);

            // Fetch time slots for this branch
            const timeSlotsSnap = await getDocs(
              query(collection(db, "timeSlots"), where("branch", "==", userData.branch))
            );
            setTimeSlots(timeSlotsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as TimeSlot[]);

            // Fetch scheduled slots for this branch
            const scheduledSnap = await getDocs(collection(db, "scheduledSlots"));
            setScheduledSlots(scheduledSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as ScheduledSlot[]);
          }
        }
      } catch (err: any) {
        setError("Failed to load data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAddTimeSlot = async () => {
    if (!auth.currentUser) {
      setError("Please log in to add time slots.");
      return;
    }

    // Determine which days to create slots for
    const daysToCreate = selectedDays.length > 0 ? selectedDays : [newSlot.dayOfWeek];
    
    if (daysToCreate.length === 0 || !newSlot.startTime || !newSlot.endTime) {
      setError("Please fill all fields and select at least one day.");
      return;
    }

    if (newSlot.startTime >= newSlot.endTime) {
      setError("End time must be after start time.");
      return;
    }

    setLoading(true);
    try {
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      const userData = userDoc.data();
      
      // Create time slots for each selected day
      for (const day of daysToCreate) {
        await addDoc(collection(db, "timeSlots"), {
          branch: userData?.branch,
          dayOfWeek: day,
          startTime: newSlot.startTime,
          endTime: newSlot.endTime,
          maxStudents: newSlot.maxStudents,
          createdAt: new Date()
        });
      }

      // Refresh time slots
      const timeSlotsSnap = await getDocs(
        query(collection(db, "timeSlots"), where("branch", "==", userData?.branch))
      );
      setTimeSlots(timeSlotsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as TimeSlot[]);

      setShowAddDialog(false);
      setNewSlot({ dayOfWeek: "", startTime: "", endTime: "", maxStudents: 1 });
      setSelectedDays([]);
    } catch (err) {
      setError("Failed to add time slot(s). Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTimeSlot = async (slotId: string) => {
    if (!confirm("Are you sure you want to delete this time slot?")) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, "timeSlots", slotId));
      setTimeSlots(prev => prev.filter(slot => slot.id !== slotId));
    } catch (err) {
      setError("Failed to delete time slot. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyTimeSlot = async () => {
    if (!slotToCopy || copyToDays.length === 0 || !auth.currentUser) {
      setError("Please select days to copy the time slot to.");
      return;
    }

    setLoading(true);
    try {
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      const userData = userDoc.data();
      
      // Create copies of the time slot for each selected day
      for (const day of copyToDays) {
        // Skip if the slot already exists for this day
        const existingSlot = timeSlots.find(
          slot => slot.dayOfWeek === day && 
                  slot.startTime === slotToCopy.startTime && 
                  slot.endTime === slotToCopy.endTime
        );
        
        if (!existingSlot) {
          await addDoc(collection(db, "timeSlots"), {
            branch: userData?.branch,
            dayOfWeek: day,
            startTime: slotToCopy.startTime,
            endTime: slotToCopy.endTime,
            maxStudents: slotToCopy.maxStudents,
            createdAt: new Date()
          });
        }
      }

      // Refresh time slots
      const timeSlotsSnap = await getDocs(
        query(collection(db, "timeSlots"), where("branch", "==", userData?.branch))
      );
      setTimeSlots(timeSlotsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as TimeSlot[]);

      setShowCopyDialog(false);
      setSlotToCopy(null);
      setCopyTodays([]);
    } catch (err) {
      setError("Failed to copy time slot. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditTimeSlot = async () => {
    if (!slotToEdit || !editSlot.dayOfWeek || !editSlot.startTime || !editSlot.endTime || !auth.currentUser) {
      setError("Please fill all fields.");
      return;
    }

    if (editSlot.startTime >= editSlot.endTime) {
      setError("End time must be after start time.");
      return;
    }

    setLoading(true);
    try {
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      const userData = userDoc.data();
      
      // Update the time slot
      await updateDoc(doc(db, "timeSlots", slotToEdit.id), {
        dayOfWeek: editSlot.dayOfWeek,
        startTime: editSlot.startTime,
        endTime: editSlot.endTime,
        maxStudents: editSlot.maxStudents,
        updatedAt: new Date()
      });

      // Refresh time slots
      const timeSlotsSnap = await getDocs(
        query(collection(db, "timeSlots"), where("branch", "==", userData?.branch))
      );
      setTimeSlots(timeSlotsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as TimeSlot[]);

      setShowEditDialog(false);
      setSlotToEdit(null);
      setEditSlot({ dayOfWeek: "", startTime: "", endTime: "", maxStudents: 1 });
    } catch (err) {
      setError("Failed to update time slot. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getSlotOccupancy = (slotId: string, date: string) => {
    const occupied = scheduledSlots.filter(
      scheduled => 
        scheduled.timeSlotId === slotId && 
        scheduled.date === date && 
        scheduled.status === "scheduled"
    ).length;
    const timeSlot = timeSlots.find(slot => slot.id === slotId);
    return { occupied, max: timeSlot?.maxStudents || 1 };
  };

  const getWeekDates = () => {
    const today = new Date();
    const startOfCurrentWeek = startOfWeek(today, { weekStartsOn: 1 }); // Monday
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(addDays(startOfCurrentWeek, i));
    }
    return dates;
  };

  const groupedSlots = timeSlots.reduce((acc, slot) => {
    if (!acc[slot.dayOfWeek]) {
      acc[slot.dayOfWeek] = [];
    }
    acc[slot.dayOfWeek].push(slot);
    return acc;
  }, {} as Record<string, TimeSlot[]>);

  // Sort time slots by start time for each day
  Object.keys(groupedSlots).forEach(day => {
    groupedSlots[day].sort((a, b) => {
      // Convert time strings to comparable format (HH:MM)
      const timeA = a.startTime.padStart(5, '0');
      const timeB = b.startTime.padStart(5, '0');
      return timeA.localeCompare(timeB);
    });
  });

  const weekDates = getWeekDates();

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Time Slot Management</h1>
        <Button onClick={() => setShowAddDialog(true)}>
          Add Time Slot
        </Button>
      </div>

      {branch && (
        <div className="bg-gray-100 p-4 rounded mb-6">
          <span className="text-lg">Branch: <b>{branch}</b></span>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div>Loading time slots...</div>
      ) : (
        <div className="space-y-6">
          {/* Weekly Schedule View */}
          <Card>
            <CardHeader>
              <CardTitle>Weekly Schedule Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-7 gap-2 lg:gap-4">
                {weekDates.map((date, index) => {
                  const dayName = daysOfWeek[index].value;
                  const daySlots = groupedSlots[dayName] || [];
                  
                  return (
                    <div key={date.toISOString()} className="border rounded p-1.5 lg:p-2 min-w-0">
                      <div className="font-semibold text-center mb-1 text-xs lg:text-sm">
                        {daysOfWeek[index].label}
                      </div>
                      <div className="text-[10px] lg:text-xs text-center text-gray-500 mb-2">
                        {format(date, "MMM dd")}
                      </div>
                      <div className="space-y-1">
                        {daySlots.map(slot => {
                          const occupancy = getSlotOccupancy(slot.id, format(date, "yyyy-MM-dd"));
                          const isAvailable = occupancy.occupied < occupancy.max;
                          
                          return (
                            <div
                              key={slot.id}
                              className={`p-1.5 lg:p-2 rounded min-w-0 ${
                                isAvailable
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              <div className="font-medium text-[8px] lg:text-[10px] leading-tight text-center whitespace-nowrap">
                                {formatTime12Hour(slot.startTime)} - {formatTime12Hour(slot.endTime)}
                              </div>
                              <div className="text-[7px] lg:text-[9px] leading-none mt-0.5 text-center">
                                {occupancy.occupied}/{occupancy.max} students
                              </div>
                            </div>
                          );
                        })}
                        {daySlots.length === 0 && (
                          <div className="text-xs text-gray-400 text-center">
                            No slots
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Time Slots Management */}
          <Card>
            <CardHeader>
              <CardTitle>Manage Time Slots</CardTitle>
            </CardHeader>
            <CardContent>
              {timeSlots.length === 0 ? (
                <p>No time slots created yet. Click "Add Time Slot" to create one.</p>
              ) : (
                <div className="space-y-4">
                  {daysOfWeek.map(day => {
                    const daySlots = groupedSlots[day.value] || [];
                    if (daySlots.length === 0) return null;

                    return (
                      <div key={day.value} className="border rounded p-4">
                        <h3 className="font-semibold mb-3">{day.label}</h3>
                        <div className="space-y-2">
                          {daySlots.map(slot => (
                            <div key={slot.id} className="flex justify-between items-center bg-gray-50 p-3 rounded">
                              <div>
                                <span className="font-medium">
                                  {formatTime12Hour(slot.startTime)} - {formatTime12Hour(slot.endTime)}
                                </span>
                                <span className="text-sm text-gray-600 ml-2">
                                  (Max {slot.maxStudents} students)
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSlotToEdit(slot);
                                    setEditSlot({
                                      dayOfWeek: slot.dayOfWeek,
                                      startTime: slot.startTime,
                                      endTime: slot.endTime,
                                      maxStudents: slot.maxStudents
                                    });
                                    setShowEditDialog(true);
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSlotToCopy(slot);
                                    setShowCopyDialog(true);
                                  }}
                                >
                                  Copy
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleDeleteTimeSlot(slot.id)}
                                  disabled={loading}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Time Slot Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Time Slot</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Day of Week</label>
              <div className="space-y-2">
                <div className="text-sm text-gray-600">Select one day or multiple days:</div>
                <div className="grid grid-cols-2 gap-2">
                  {daysOfWeek.map(day => (
                    <label key={day.value} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedDays.includes(day.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDays(prev => [...prev, day.value]);
                          } else {
                            setSelectedDays(prev => prev.filter(d => d !== day.value));
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">{day.label}</span>
                    </label>
                  ))}
                </div>
                {selectedDays.length > 0 && (
                  <div className="text-sm text-blue-600">
                    Selected: {selectedDays.map(d => daysOfWeek.find(day => day.value === d)?.label).join(", ")}
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Start Time</label>
                <Input
                  type="time"
                  value={newSlot.startTime}
                  onChange={(e) => setNewSlot(prev => ({ ...prev, startTime: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">End Time</label>
                <Input
                  type="time"
                  value={newSlot.endTime}
                  onChange={(e) => setNewSlot(prev => ({ ...prev, endTime: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Maximum Students</label>
              <Input
                type="number"
                min="1"
                max="10"
                value={newSlot.maxStudents}
                onChange={(e) => setNewSlot(prev => ({ ...prev, maxStudents: parseInt(e.target.value) || 1 }))}
              />
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddDialog(false);
                  setNewSlot({ dayOfWeek: "", startTime: "", endTime: "", maxStudents: 1 });
                  setSelectedDays([]);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddTimeSlot}
                disabled={loading || selectedDays.length === 0 || !newSlot.startTime || !newSlot.endTime}
              >
                {loading ? "Adding..." : `Add Time Slot${selectedDays.length > 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Copy Time Slot Dialog */}
      <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy Time Slot</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {slotToCopy && (
              <div className="p-3 bg-gray-100 rounded">
                <div className="font-medium">Time Slot to Copy:</div>
                <div className="text-sm text-gray-600">
                  {daysOfWeek.find(d => d.value === slotToCopy.dayOfWeek)?.label} - {formatTime12Hour(slotToCopy.startTime)} to {formatTime12Hour(slotToCopy.endTime)} (Max {slotToCopy.maxStudents} students)
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium mb-2">Copy to days:</label>
              <div className="grid grid-cols-2 gap-2">
                {daysOfWeek.map(day => {
                  const isCurrentDay = day.value === slotToCopy?.dayOfWeek;
                  const existingSlot = timeSlots.find(
                    slot => slot.dayOfWeek === day.value && 
                            slot.startTime === slotToCopy?.startTime && 
                            slot.endTime === slotToCopy?.endTime
                  );
                  
                  return (
                    <label key={day.value} className={`flex items-center space-x-2 cursor-pointer ${isCurrentDay ? 'opacity-50' : ''}`}>
                      <input
                        type="checkbox"
                        checked={copyToDays.includes(day.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCopyTodays(prev => [...prev, day.value]);
                          } else {
                            setCopyTodays(prev => prev.filter(d => d !== day.value));
                          }
                        }}
                        disabled={isCurrentDay || !!existingSlot}
                        className="rounded"
                      />
                      <span className="text-sm">
                        {day.label}
                        {isCurrentDay && <span className="text-gray-500"> (original)</span>}
                        {existingSlot && !isCurrentDay && <span className="text-orange-500"> (exists)</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
              {copyToDays.length > 0 && (
                <div className="text-sm text-blue-600 mt-2">
                  Will copy to: {copyToDays.map(d => daysOfWeek.find(day => day.value === d)?.label).join(", ")}
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCopyDialog(false);
                  setSlotToCopy(null);
                  setCopyTodays([]);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCopyTimeSlot}
                disabled={loading || copyToDays.length === 0}
              >
                {loading ? "Copying..." : `Copy to ${copyToDays.length} day${copyToDays.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Time Slot Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Time Slot</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {slotToEdit && (
              <div className="p-3 bg-gray-100 rounded">
                <div className="font-medium">Editing Time Slot:</div>
                <div className="text-sm text-gray-600">
                  {daysOfWeek.find(d => d.value === slotToEdit.dayOfWeek)?.label} - {formatTime12Hour(slotToEdit.startTime)} to {formatTime12Hour(slotToEdit.endTime)} (Max {slotToEdit.maxStudents} students)
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium mb-1">Day of Week</label>
              <Select
                value={editSlot.dayOfWeek}
                onValueChange={(value) => setEditSlot(prev => ({ ...prev, dayOfWeek: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  {daysOfWeek.map(day => (
                    <SelectItem key={day.value} value={day.value}>
                      {day.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Start Time</label>
                <Input
                  type="time"
                  value={editSlot.startTime}
                  onChange={(e) => setEditSlot(prev => ({ ...prev, startTime: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">End Time</label>
                <Input
                  type="time"
                  value={editSlot.endTime}
                  onChange={(e) => setEditSlot(prev => ({ ...prev, endTime: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Maximum Students</label>
              <Input
                type="number"
                min="1"
                max="10"
                value={editSlot.maxStudents}
                onChange={(e) => setEditSlot(prev => ({ ...prev, maxStudents: parseInt(e.target.value) || 1 }))}
              />
            </div>
            
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditDialog(false);
                  setSlotToEdit(null);
                  setEditSlot({ dayOfWeek: "", startTime: "", endTime: "", maxStudents: 1 });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditTimeSlot}
                disabled={loading || !editSlot.dayOfWeek || !editSlot.startTime || !editSlot.endTime}
              >
                {loading ? "Updating..." : "Update Time Slot"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffTimeSlots;
